import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Send, FileText, Tag, Database, Loader2, ArrowRight, BookOpen, TrendingUp, AlertTriangle, Copy } from "lucide-react";

interface IntegrationStatus {
  apiKeys: { exportKey: boolean; taggingKey: boolean };
  questions: { totalInSystem: number; fromQuizzative: number; withObjectives: number; untagged: number };
}

interface SubjectReport {
  name: string;
  summary: string;
  total_questions: number;
  correct: number;
  score_percent: number;
  strands: Array<{ name: string; score_percent: number; status: string }>;
  can_do: string[];
  working_on: string[];
  needs_help: string[];
  parent_tips: string[];
}

interface StudentReport {
  student: { id: string; name: string; grade_level: string };
  report_date: string;
  subjects: SubjectReport[];
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
      <span className="text-sm">{label}</span>
    </div>
  );
}

function StrandBar({ name, percent, status }: { name: string; percent: number; status: string }) {
  const color = status === "strong" ? "bg-green-500" : status === "developing" ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span>{name}</span>
        <span className="font-medium">{percent}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function ReportCard({ subject }: { subject: SubjectReport }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">{subject.name}</CardTitle>
          <Badge variant={subject.score_percent >= 80 ? "default" : subject.score_percent >= 50 ? "secondary" : "destructive"}>
            {subject.score_percent}%
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{subject.summary}</p>
        <div className="text-xs text-muted-foreground">{subject.correct}/{subject.total_questions} correct</div>
      </CardHeader>
      <CardContent className="space-y-4">
        {subject.strands.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium uppercase text-muted-foreground">Strand Breakdown</h4>
            {subject.strands.map((s) => (
              <StrandBar key={s.name} name={s.name} percent={s.score_percent} status={s.status} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {subject.can_do.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Can Do
              </h4>
              <ul className="text-xs space-y-0.5">
                {subject.can_do.map((item, i) => <li key={i} className="text-muted-foreground">{item}</li>)}
              </ul>
            </div>
          )}
          {subject.working_on.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Working On
              </h4>
              <ul className="text-xs space-y-0.5">
                {subject.working_on.map((item, i) => <li key={i} className="text-muted-foreground">{item}</li>)}
              </ul>
            </div>
          )}
          {subject.needs_help.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Needs Help
              </h4>
              <ul className="text-xs space-y-0.5">
                {subject.needs_help.map((item, i) => <li key={i} className="text-muted-foreground">{item}</li>)}
              </ul>
            </div>
          )}
        </div>

        {subject.parent_tips.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <BookOpen className="h-3 w-3" /> Tips for Parents
            </h4>
            <ul className="text-xs space-y-0.5">
              {subject.parent_tips.map((tip, i) => <li key={i} className="text-muted-foreground">{tip}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function QuizzativeIntegration() {
  const { toast } = useToast();
  const [reportStudentId, setReportStudentId] = useState("");
  const [sendStudentId, setSendStudentId] = useState("");

  const statusQuery = useQuery<IntegrationStatus>({
    queryKey: ["/api/admin/quizzative/integration-status"],
    refetchInterval: 30000,
  });

  const reportMutation = useMutation({
    mutationFn: async (studentId: string) => {
      const res = await apiRequest("GET", `/api/admin/quizzative/student-report/${studentId}`);
      return res.json() as Promise<StudentReport>;
    },
    onError: (err: any) => {
      toast({ title: "Report Error", description: err.message || "Failed to fetch report", variant: "destructive" });
    },
  });

  const sendResultsMutation = useMutation({
    mutationFn: async (studentId: string) => {
      const res = await apiRequest("POST", "/api/admin/quizzative/send-results", { studentId });
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Results Sent", description: `${data.received || 0} results sent to Quizzative` });
    },
    onError: (err: any) => {
      toast({ title: "Send Error", description: err.message || "Failed to send results", variant: "destructive" });
    },
  });

  const backfillMutation = useMutation({
    mutationFn: async (studentId?: string) => {
      const res = await apiRequest("POST", "/api/admin/quizzative/backfill-results", studentId ? { studentId } : {});
      return res.json() as Promise<{ totalSent: number; totalStudents: number; errors: number; skipped: number }>;
    },
    onSuccess: (data) => {
      toast({ title: "Backfill Complete", description: `Sent ${data.totalSent} results for ${data.totalStudents} students (${data.skipped} non-Quizzative skipped, ${data.errors} errors)` });
    },
    onError: (err: any) => {
      toast({ title: "Backfill Error", description: err.message || "Failed to backfill results", variant: "destructive" });
    },
  });

  const status = statusQuery.data;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Quizzative Integration</h2>
        <p className="text-sm text-muted-foreground">Manage the connection between EduFocal Academy and Quizzative question bank</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> API Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusBadge ok={status?.apiKeys.exportKey ?? false} label="Export API Key (Question Sync + Reports)" />
            <StatusBadge ok={status?.apiKeys.taggingKey ?? false} label="Tagging API Key (Curriculum Tagging)" />
            {status?.apiKeys.taggingKey && (
              <div className="mt-3 p-2 rounded-md bg-muted text-xs">
                <p className="text-muted-foreground mb-1">Tagging endpoints available for Quizzative to call:</p>
                <code className="block">GET /api/quizzative/untagged-questions</code>
                <code className="block">POST /api/quizzative/apply-tags</code>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4" /> Question Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {statusQuery.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : status ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-2xl font-bold">{status.questions.totalInSystem}</div>
                  <div className="text-xs text-muted-foreground">Total Questions</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{status.questions.fromQuizzative}</div>
                  <div className="text-xs text-muted-foreground">From Quizzative</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{status.questions.withObjectives}</div>
                  <div className="text-xs text-muted-foreground">With Objectives</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{status.questions.untagged}</div>
                  <div className="text-xs text-muted-foreground">Untagged</div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" /> Send Student Results</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Results are automatically sent when students answer Quizzative questions. Use this to manually send all historical results for a student.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Student ID"
              value={sendStudentId}
              onChange={(e) => setSendStudentId(e.target.value)}
              className="max-w-xs"
            />
            <Button
              onClick={() => sendResultsMutation.mutate(sendStudentId)}
              disabled={!sendStudentId || sendResultsMutation.isPending}
            >
              {sendResultsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send Results
            </Button>
          </div>
          {sendResultsMutation.data && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
              Sent {sendResultsMutation.data.received} results for {sendResultsMutation.data.student_id}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> Backfill Historical Results</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Send ALL historical question attempts to Quizzative for all students (or a specific student). This ensures Quizzative has complete data for accurate progress reports.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Student ID (leave empty for all)"
              value={sendStudentId}
              onChange={(e) => setSendStudentId(e.target.value)}
              className="max-w-xs"
            />
            <Button
              onClick={() => {
                if (!sendStudentId && !confirm("This will send ALL historical results for ALL students to Quizzative. This may take several minutes. Continue?")) return;
                backfillMutation.mutate(sendStudentId || undefined);
              }}
              disabled={backfillMutation.isPending}
              variant="default"
            >
              {backfillMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Database className="h-4 w-4 mr-1" />}
              {backfillMutation.isPending ? "Backfilling..." : sendStudentId ? "Backfill Student" : "Backfill All Students"}
            </Button>
          </div>
          {backfillMutation.data && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
              Sent {backfillMutation.data.totalSent} results for {backfillMutation.data.totalStudents} students ({backfillMutation.data.errors} errors)
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Student Report Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Fetch an AI-generated curriculum report for a student from Quizzative. The student must have answered at least 5 Quizzative questions per subject.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Student ID"
              value={reportStudentId}
              onChange={(e) => setReportStudentId(e.target.value)}
              className="max-w-xs"
            />
            <Button
              onClick={() => reportMutation.mutate(reportStudentId)}
              disabled={!reportStudentId || reportMutation.isPending}
            >
              {reportMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              Fetch Report
            </Button>
          </div>

          {reportMutation.data && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{reportMutation.data.student.name}</Badge>
                <Badge variant="outline">{reportMutation.data.student.grade_level}</Badge>
                <span className="text-xs text-muted-foreground">Report date: {reportMutation.data.report_date}</span>
              </div>
              {(reportMutation.data.subjects || []).map((subj) => (
                <ReportCard key={subj.name} subject={subj} />
              ))}
            </div>
          )}

          {reportMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              Could not fetch report. The student may not have answered enough Quizzative questions yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
